import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { writeBlobToNativeCache } from './ucOriginalDownload';

// Generic "share this photo" trigger for photo-preview popups - fetches the
// same URL the popup's own "Download original" button already uses and
// hands it to the OS share sheet (WhatsApp/Facebook/etc. are just entries in
// that sheet - there's no need for per-network buttons/SDKs).
//
// Native: navigator.share isn't implemented by Android's WebView (see
// EventPage.js's EventHeader.handleShare), so this writes the blob to a
// cache file via the same helper the native download path uses, then hands
// @capacitor/share a file:// uri - it converts that to a content:// uri via
// the app's own FileProvider (already configured for the whole cache dir,
// see android/app/src/main/res/xml/file_paths.xml) before building the
// share intent.
//
// Web: mirrors SharePage.js's existing sharePhoto() - share the actual file
// via the Web Share API's `files` when supported, else share the link, else
// fall back to copying the link to the clipboard.
export async function sharePhoto(url, filename, { title, text } = {}) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Could not load photo to share: ${res.status}`);
  const blob = await res.blob();

  if (Capacitor.isNativePlatform()) {
    const { uri, cleanup } = await writeBlobToNativeCache(blob, filename);
    try {
      await Share.share({ title, text, files: [uri] });
    } finally {
      cleanup();
    }
    return;
  }

  const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    await navigator.share({ title, text, files: [file] });
    return;
  }
  if (navigator.share) {
    await navigator.share({ title, text, url });
    return;
  }
  await navigator.clipboard.writeText(url);
}
